import React, { useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  RideButton,
  RideSkeleton,
  RideActionButton,
  RideGlDeleteSolid,
  RideGlUserSolid,
} from "@rimac-seguros/ride-system-components";
import { DateTime } from "luxon";

import useResponsiveDetection from "@/hooks/useResponsiveDetection";
import useBreakpoint from "@/hooks/useBreakpoint";
import { addBeneficiary } from "@/redux/actions/beneficiaries";
import { FormBasicInformation } from "../FormBasicInformation";
import { FormAdditionalData } from "../FormAdditionalData";
import { FormUbigeo } from "../FormUbigeo";
import { DetailBeneficiary } from "../DetailBeneficiary";
import { FormDocument } from "../FormDocument";
import { useValidatePerson } from "@/hooks/useValidatePerson";
import { ModalError } from "@/components/molecules/ModalError";
import { formatAndMaskFullName, formatFullName } from "@/utils/format-name";
import helpers, { sendTagChain } from "@/utils/helpers";
import { focusFirstErrorField } from '@/utils/Utils'

export const CardFormBeneficiary = ({
  index,
  register,
  watch,
  errors,
  getValues,
  trigger,
  setValue,
  setError,
  resetField,
  clearErrors,
  isVCD,
  product,
  onHandleRemove,
  getFieldState,
  onSavingChange,
  isAnyCardSaving,
}) => {
  const isVE = helpers.validateVidaEndosable();
  const [hasFetchedPersonData, setHasFetchedPersonData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [isValidBasicInformation, setIsValidBasicInformation] = useState(false);
  const [isValidAdditionalData, setIsValidAdditionalData] = useState(false);
  const [isValidUbigeo, setIsValidUbigeo] = useState(false);
  const [isValidDocument, setIsValidDocument] = useState(false);

  const { relationshipList } = useSelector((state) => state.global);

  const prefixControl = `beneficiaries.${index}`;
  const prefixError = errors?.beneficiaries?.[index];
  const {
    names,
    lastName,
    surName,
    status,
    origin,
    relationship,
    percentage,
  } = watch(`${prefixControl}`);

  const dispatch = useDispatch();
  const isDesktop = useBreakpoint("large-min");

  const { isSmallScreen } = useResponsiveDetection();
  const {
    showModalInvalidData,
    validatePerson,
    setShowModalInvalidData,
    resetAttempts,
  } = useValidatePerson(
    watch(`${prefixControl}.documentType`),
    watch(`${prefixControl}.documentNumber`),
    isVE
      ? "/comprar/seguro-vida-endoso-con-devolucion/gracias-validacion"
      : "/comprar/seguro-vida-devolucion-garantizada/gracias-ncliente"
  );
  const fullNameFormatter = isVE ? formatAndMaskFullName : formatFullName;

  const handleSaveBeneficiary = async () => {
    if (loadingSave) return;

    const isValid =await trigger(prefixControl);

    if (!isValid) {
      const fieldErrors = { ...errors.beneficiaries?.[index] }

      sendTagChain(
        Object.values(fieldErrors)
          .map(e => e.message)
          .filter(Boolean)
          .map(message => ({
            type: 'getTagLog',
            message
          }))
      )
      focusFirstErrorField({ getFieldState })
    }

    if (Object.keys(errors.beneficiaries?.[index] || {}).length) {
      return;
    }

    if (!isValidDocument) return;

    setLoadingSave(true);
    onSavingChange?.(true);

    const rawBeneficiary = getValues(`beneficiaries[${index}]`);
    const trimmedBeneficiary = Object.fromEntries(
      Object.entries(rawBeneficiary).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );

    if (origin) {
      setValue(`${prefixControl}.status`, 1);
      dispatch(
        addBeneficiary({
          ...trimmedBeneficiary,
          status: 1,
        })
      );
      mappingAnalyticsTags();
      setLoadingSave(false);
      onSavingChange?.(false);
      return;
    }

    try {
      const birthdate = watch(`${prefixControl}.birthdate`);
      const { fieldsWithError, validateStatus } = await validatePerson({
        ...trimmedBeneficiary,
        birthdate: DateTime.fromFormat(birthdate, "dd/MM/yyyy").toFormat(
          "yyyy-MM-dd"
        ),
      });

      if (validateStatus === "RECHAZADO") {
        validateFieldsToSetError(fieldsWithError);
        helpers.getTagLog(
          "Datos incorrectos formulario beneficiarios",
          "trackLog"
        );
        return;
      }

      setValue(`${prefixControl}.status`, 1);
      dispatch(
        addBeneficiary({
          ...trimmedBeneficiary,
          status: 1,
        })
      );
      mappingAnalyticsTags();
    } finally {
      setLoadingSave(false);
      onSavingChange?.(false);
    }
  };

  const validateFieldsToSetError = (fields = []) => {
    if (fields.length > 0) {
      setTimeout(() => {
        const fieldMap = {
          nombres: "names",
          apepat: "lastName",
          apemat: "surName",
          sexo: "gender",
          fecnac: "birthdate",
        };

        fields.forEach((item) => {
          const errorField = fieldMap[item];
          if (errorField) {
            setError(`${prefixControl}.${errorField}`, {
              type: "custom",
              message: "Error en el campo",
            });
          }
        });
      }, 300);
    }
  };

  const getRelationship = useMemo(() => {
    return relationshipList.find((_) => _.id === Number(relationship))
      ?.descripcion;
  }, [relationshipList, relationship]);

  const mappingAnalyticsTags = () => {
    let prefixLabel = "Sin";
    let textWithoutUbigeo = "";
    let textWithoutContact = "";

    if (origin) {
      prefixLabel = "Con";
      textWithoutUbigeo = isValidUbigeo ? "" : "|sin ubigeo";
      textWithoutContact = isValidAdditionalData ? "" : "|sin contacto";
    }

    let label = `${prefixLabel} datos cliente${textWithoutUbigeo}${textWithoutContact}`;
    label = isVE ? label : `${label} - ${getRelationship} - ${percentage}%`;

    if (!origin) {
      helpers.getTagChannel(
        product,
        "Datos personales Beneficiario – Sin datos cliente",
        "trackFormSubmit"
      );
    }
    
    helpers.setActionTag({
      group: product,
      category: isVCD || isVE ? "Elegir beneficiarios" : "Interacciones",
      action: isVCD || isVE ? "click" : "Click",
      label: isVCD || isVE ? label : `Elegir beneficiarios - ${label}`,
    });
  };

  return (
    <div
      className={`border-t border-b border-ride-sys-color-border-default py-ride-gl-spacing-5xlarge lg:rounded-ride-sys-border-radius-container-large lg:pt-ride-gl-spacing-4xlarge lg:border lg:px-ride-gl-spacing-5xlarge ${
        status === 0 && index === 0
          ? "border-t-ride-gl-border-width-none pt-0"
          : ""
      } ${status === 1 &&
        "rounded-ride-sys-border-radius-container-large border-none bg-ride-sys-color-surface-subtle p-ride-gl-spacing-2xlarge pb-ride-gl-spacing-4xlarge lg:py-ride-gl-spacing-4xlarge"}`}
    >
      <div
        className={`flex items-center justify-between h-8 lg:h-10 ${
          status === 1
            ? "mb-ride-gl-spacing-2xlarge lg:mb-ride-gl-spacing-3xlarge"
            : "mb-ride-gl-spacing-4xlarge"
        }`}
      >
        <h2
          className={`${
            status === 1
              ? "ride-sys-text-label-small-default lg:ride-sys-text-label-large-default"
              : "ride-sys-text-label-xlarge-default lg:ride-sys-text-label-2xlarge-default"
          }`}
        >
          Beneficiario N°{index + 1}
        </h2>
        {(status === 1 || watch("beneficiaries").length > 1) && (
          <div className="relative">
            <RideActionButton
              glyph={<RideGlDeleteSolid />}
              size={isDesktop ? "medium" : "small"}
              tooltipText="Eliminar"
              onClick={onHandleRemove}
              disabled={isAnyCardSaving}
            />
            { isAnyCardSaving && (
              <RideSkeleton
                type="rectangle"
                width="100%"
                height="100%"
                className="absolute top-0 left-0 z-[1]" 
              />
            )}
          </div>
        )}
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {status === 0 ? (
          <>
            <FormDocument
              index={index}
              register={register}
              prefixControl={prefixControl}
              prefixError={prefixError}
              watch={watch}
              getValues={getValues}
              loadingSave={loadingSave}
              setValue={setValue}
              setError={setError}
              resetField={resetField}
              setLoading={setLoading}
              setHasFetchedPersonData={setHasFetchedPersonData}
              setIsValidBasicInformation={setIsValidBasicInformation}
              setIsValidAdditionalData={setIsValidAdditionalData}
              setIsValidUbigeo={setIsValidUbigeo}
              setIsValidDocument={setIsValidDocument}
              resetAttempts={resetAttempts}
              isVCD={isVCD}
              product={product}
            />
            <div className="grid gap-ride-gl-spacing-2xlarge mt-ride-gl-spacing-2xlarge lg:gap-ride-gl-spacing-3xlarge lg:mt-ride-gl-spacing-3xlarge">
              {!loading && isValidDocument && hasFetchedPersonData && (
                <>
                  {!isValidBasicInformation && (
                    <FormBasicInformation
                      register={register}
                      prefixControl={prefixControl}
                      prefixError={prefixError}
                      loadingSave={loadingSave}
                    />
                  )}
                  {isValidBasicInformation && (
                    <div className="flex p-ride-gl-spacing-xlarge gap-ride-gl-spacing-large bg-ride-sys-color-surface-disable rounded-ride-sys-border-radius-container-medium lg:p-ride-gl-spacing-2xlarge">
                      <RideGlUserSolid size="medium" />
                      <div className="flex flex-col gap-ride-gl-spacing-small">
                        <h4 className="h-5 ride-sys-text-label-medium-semibold">
                          Nombres y apellidos
                        </h4>
                        <span className="ride-sys-text-paragraph-large-regular-short">
                          {fullNameFormatter(names, lastName, surName)}
                        </span>
                      </div>
                    </div>
                  )}
                  {!isValidUbigeo && (
                    <FormUbigeo
                      register={register}
                      prefixControl={prefixControl}
                      prefixError={prefixError}
                      setValue={setValue}
                      watch={watch}
                      isSmallScreen={isSmallScreen}
                      loadingSave={loadingSave}
                    />
                  )}
                </>
              )}

              {!loading && (
                <FormAdditionalData
                  register={register}
                  prefixControl={prefixControl}
                  prefixError={prefixError}
                  watch={watch}
                  showPhoneAndEmail={
                    hasFetchedPersonData &&
                    !isValidAdditionalData &&
                    isValidDocument
                  }
                  isSmallScreen={isSmallScreen}
                  loadingSave={loadingSave}
                  setValue={setValue}
                />
              )}
              {loading && (
                <div className="grid gap-ride-gl-spacing-2xlarge lg:grid-cols-2">
                  <RideSkeleton type="rectangle" width="100%" height="72px" />
                  <RideSkeleton type="rectangle" width="100%" height="72px" />
                </div>
              )}
              <div className="flex justify-end">
                <RideButton
                  text="Guardar"
                  size={isDesktop ? "large" : "x-large"}
                  variant="outline"
                  disabled={loading}
                  loading={loadingSave}
                  onClick={handleSaveBeneficiary}
                  className="w-full mt-2 md:w-auto lg:mt-1"
                />
              </div>
            </div>
          </>
        ) : (
          <DetailBeneficiary
            index={index}
            register={register}
            prefixControl={prefixControl}
            prefixError={prefixError}
            errors={errors}
            getValues={getValues}
            setError={setError}
            clearErrors={clearErrors}
            trigger={trigger}
          />
        )}
      </form>

      <ModalError
        open={showModalInvalidData}
        onClose={() => setShowModalInvalidData(false)}
        title="Datos incorrectos"
        message="Por favor, revisa los datos e inténtalo nuevamente."
        icon="RideFiDniErrorLight"
        buttonText="Reintentar"
      />
    </div>
  );
};
